import type { SagaIterator } from 'redux-saga';
import { put, select, takeEvery } from 'redux-saga/effects';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { ResourceActionCreator, ResourceFailureActionCreator } from '../core/resourceStoreTypes';
import { entitlementsSelectors } from '../entitlements';
import { featureFlagsSelectors } from '../feature-flags';
import * as actions from './businessActions';
import { type Business, type BusinessProps, mkBusiness } from './businessTypes';

const mkQcsSyncBusinessesConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<BusinessProps>();
export const transformResponseToBusinesses = (
  response?: {
    data?: { resources?: BusinessProps[] };
  } | null,
): Business[] | undefined => response?.data?.resources?.map(mkBusiness);

export const mkResourceConfig = (
  successAction: ResourceActionCreator<BusinessProps>,
  failureAction: ResourceFailureActionCreator,
) =>
  mkQcsSyncBusinessesConfig({
    resourceName: 'business',
    resourceBaseUrl: '/businesses',
    transformResponseToResources: transformResponseToBusinesses,
    successAction,
    failureAction,
    applyResourceFromCRUDResponse: true,
  });

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('businessDumpUpdater');
export function* businessDumpUpdater(resources: Array<Business>) {
  yield callFirst(businessDumpUpdaterFull, resources);
  yield callLast(businessNudgeUpdater);
}

function* businessDumpUpdaterFull(resources: Array<Business>): SagaIterator {
  const bizEnabled: boolean = yield select(entitlementsSelectors.getBusinessEnabled);
  const bizBump: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpBusiness');
  if (bizEnabled && bizBump) {
    const businesses = resources.map((resource: Business) => mkBusiness(resource));
    yield put(actions.updateBusinessBump({ resources: businesses }));
  }
}

export function* businessNudgeUpdater(): SagaIterator {
  const bizEnabled: boolean = yield select(entitlementsSelectors.getBusinessEnabled);
  const bizBump: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpBusiness');
  if (bizEnabled && bizBump) {
    yield put(actions.getBusiness(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

export function* getBusinessActionWatcher() {
  const config = mkResourceConfig(actions.getBusinessSuccess, actions.getBusinessFailure);
  yield takeEvery(actions.getBusiness.type, resourceSagaUtils.qcsSyncGetResources<BusinessProps>, config);
}
export function* createBusinessActionWatcher() {
  const config = mkResourceConfig(actions.createBusinessSuccess, actions.createBusinessFailure);
  yield takeEvery(actions.createBusiness.type, resourceSagaUtils.qcsSyncCreateResource<BusinessProps>, config);
}
export function* deleteBusinessActionWatcher() {
  const config = mkResourceConfig(actions.deleteBusinessSuccess, actions.deleteBusinessFailure);
  yield takeEvery(actions.deleteBusiness.type, resourceSagaUtils.qcsSyncDeleteResource<BusinessProps>, config);
}
export function* updateBusinessActionWatcher() {
  const config = mkResourceConfig(actions.updateBusinessSuccess, actions.updateBusinessFailure);
  yield takeEvery(actions.updateBusiness.type, resourceSagaUtils.qcsSyncUpdateResource<BusinessProps>, config);
}

function* batchBusinessesActionWatcher() {
  const config = mkResourceConfig(actions.batchUpdateBusinessesSuccess, actions.batchUpdateBusinessesFailure);
  yield takeEvery(actions.batchUpdateBusinesses.type, resourceSagaUtils.qcsSyncUpdateResources<BusinessProps>, config);
}

export default [
  getBusinessActionWatcher,
  createBusinessActionWatcher,
  deleteBusinessActionWatcher,
  updateBusinessActionWatcher,
  batchBusinessesActionWatcher,
];
